import { useEffect, useState } from "react";
import { ExternalLink, Flame, Users } from "lucide-react";
import { SOLSCAN_TOKEN_URL } from "../config";
import usePoll, { fmtCompact, fmtNum, fmtUsd, timeAgo } from "../hooks/usePoll";
import Reveal from "./Reveal";
import TeamLock from "./TeamLock";

function Tile({ id, label, value, sub, accent, delay, href }) {
    const Wrapper = href ? "a" : "div";
    const extra = href ? { href, target: "_blank", rel: "noopener noreferrer" } : {};
    return (
        <Reveal delay={delay} className="h-full">
            <Wrapper
                {...extra}
                data-testid={`live-stat-${id}`}
                className={`glass glass-hover relative flex h-full flex-col gap-2 overflow-hidden rounded-2xl p-5 md:p-6 ${
                    href ? "cursor-pointer" : ""
                }`}
            >
                <span className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent" />
                <span className="kicker !text-[10px]">{label}</span>
                <span
                    className={`headline text-2xl leading-none md:text-3xl ${accent || "text-white"}`}
                    data-testid={`live-stat-${id}-value`}
                >
                    {value}
                </span>
                {sub ? <span className="font-mono text-[11px] text-slate-500">{sub}</span> : null}
            </Wrapper>
        </Reveal>
    );
}

export default function LiveStats() {
    const { data, error, loading } = usePoll("/stats", 60000);
    const [, tick] = useState(0);
    useEffect(() => {
        const t = setInterval(() => tick((n) => n + 1), 10000);
        return () => clearInterval(t);
    }, []);

    const s = data && !data.error ? data : null;
    const change = s?.priceChange24h;
    const changeCls = change == null ? "text-white" : change >= 0 ? "text-green-400" : "text-red-400";
    const status = loading
        ? "CONNECTING…"
        : error || !s
          ? "LIVE DATA UNAVAILABLE — CHART BELOW STILL LIVE"
          : `${s.stale ? "CACHED · " : ""}UPDATED ${timeAgo(s.updatedAt).toUpperCase()}`;

    return (
        <div data-testid="live-stats" className="mt-12">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-2 px-1">
                <div className="flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-widest text-slate-400">
                    <span className={`h-2 w-2 rounded-full ${s ? "bg-green-400 animate-pulse-dot" : "bg-slate-500"}`} />
                    LIVE ON-CHAIN STATS
                </div>
                <span data-testid="live-stats-status" className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                    {status}
                </span>
            </div>

            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
                <Tile id="price" label="PRICE" value={fmtUsd(s?.priceUsd)} sub="DEXSCREENER" delay={0} />
                <Tile id="mcap" label="MARKET CAP" value={fmtUsd(s?.marketCap, { maximumFractionDigits: 0 })} sub="DEXSCREENER" delay={0.05} />
                <Tile
                    id="change"
                    label="24H CHANGE"
                    value={change == null ? "—" : `${change >= 0 ? "+" : ""}${change.toFixed(2)}%`}
                    accent={changeCls}
                    sub="DEXSCREENER"
                    delay={0.1}
                />
                <Tile id="volume" label="24H VOLUME" value={fmtUsd(s?.volume24h, { maximumFractionDigits: 0 })} sub={s ? `${fmtNum(s.buys24h)} BUYS · ${fmtNum(s.sells24h)} SELLS` : "DEXSCREENER"} delay={0.15} />
                <Tile
                    id="burned"
                    label="BURNED"
                    value={s?.burned != null ? fmtCompact(s.burned) : "—"}
                    accent="text-orange-400"
                    sub={s?.burnedPct != null ? `${s.burnedPct.toFixed(3)}% OF SUPPLY · SOLANA RPC` : "SOLANA RPC"}
                    delay={0.2}
                />
                {s?.holders != null ? (
                    <Tile id="holders" label="HOLDERS" value={fmtNum(s.holders)} sub="ON-CHAIN" delay={0.25} />
                ) : (
                    <Tile
                        id="holders"
                        label="HOLDERS"
                        value={
                            <span className="flex items-center gap-2 text-cyan-300">
                                <Users className="h-6 w-6" /> SOLSCAN <ExternalLink className="h-4 w-4" />
                            </span>
                        }
                        sub="VIEW LIVE HOLDER LIST"
                        href={`${SOLSCAN_TOKEN_URL}#holders`}
                        delay={0.25}
                    />
                )}
            </div>
            <TeamLock lock={s?.teamLock} />
            <p className="mt-4 flex items-center gap-2 px-1 font-mono text-[10px] uppercase tracking-widest text-slate-600">
                <Flame className="h-3 w-3 text-orange-500/70" /> Real data only — pulled from DexScreener, Solana RPC + Streamflow. Nothing here is fabricated.
            </p>
        </div>
    );
}
